import { ComponentProps, ReactNode, forwardRef, useEffect, useRef } from 'react'
import { ScrollView } from 'react-native'
import { View, ViewProps } from 'tamagui'

/**
 * Versão web de `react-native-intersection-observer`, com a mesma API.
 *
 * A lib emula o IntersectionObserver medindo layout a partir do IOScrollView:
 * indispensável no app nativo, desperdício na web (o browser tem a API de
 * verdade) e, pior, quebra. Ela publica class fields sem transpilar e o
 * interop CJS do Metro os transforma em `campo = (0, mod.default)(...)`; o
 * Safari < 16 (iOS 14 e 15) resolve o escopo desse inicializador errado
 * (https://webkit.org/b/236843) e estoura `ReferenceError: Can't find
 * variable: <var>` no constructor do IOScrollView — como ele envolve o
 * AppLayout inteiro, a árvore React cai junto e o aluno vê tela branca.
 *
 * Mantendo a assinatura igual, a troca é só de import e a lib deixa de entrar
 * no bundle web (o arquivo `.native` continua usando ela).
 */

const SCROLLABLE_OVERFLOW = /(auto|scroll|overlay)/

/**
 * Scroller mais próximo do elemento. É o equivalente em DOM do root que a lib
 * usa no nativo (o IOScrollView ancestral): sem isso, um card dentro de um
 * modal com scroll próprio contaria como visível só por estar na viewport.
 */
function findScrollRoot(node: Element): Element | null {
  let current = node.parentElement

  while (current) {
    const { overflowY, overflowX } = window.getComputedStyle(current)

    if (
      SCROLLABLE_OVERFLOW.test(overflowY) ||
      SCROLLABLE_OVERFLOW.test(overflowX)
    ) {
      return current
    }

    current = current.parentElement
  }

  return null
}

export interface IInViewProps extends Omit<ViewProps, 'onChange'> {
  onChange?: (inView: boolean) => void
  /** Fração do elemento visível (0–1) para contar como dentro. */
  threshold?: number
  /** Para de observar depois da primeira entrada. */
  triggerOnce?: boolean
  children?: ReactNode
}

export function InView({
  onChange,
  threshold = 0,
  triggerOnce = false,
  children,
  ...rest
}: IInViewProps) {
  const ref = useRef<Element | null>(null)
  const onChangeRef = useRef(onChange)
  const inViewRef = useRef<boolean | null>(null)

  useEffect(() => {
    onChangeRef.current = onChange
  }, [onChange])

  useEffect(() => {
    const node = ref.current

    if (
      !node ||
      typeof window === 'undefined' ||
      !window.IntersectionObserver
    ) {
      return
    }

    const observer = new window.IntersectionObserver(
      (entries) => {
        entries.forEach(({ isIntersecting }) => {
          // Só transições: estes callbacks disparam paginação, e o observer
          // reemite entry a cada cruzamento de threshold.
          if (inViewRef.current === isIntersecting) return

          inViewRef.current = isIntersecting
          onChangeRef.current?.(isIntersecting)

          if (isIntersecting && triggerOnce) observer.disconnect()
        })
      },
      { root: findScrollRoot(node), threshold },
    )

    observer.observe(node)

    return () => observer.disconnect()
  }, [threshold, triggerOnce])

  return (
    <View ref={ref as never} {...rest}>
      {children}
    </View>
  )
}

/**
 * Na web o IntersectionObserver do browser acha o scroller sozinho, então o
 * IOScrollView não precisa instrumentar nada: é um ScrollView comum. O
 * `scrollEventThrottle` repete o default que a lib aplicava.
 */
export const IOScrollView = forwardRef<
  ScrollView,
  ComponentProps<typeof ScrollView>
>(function IOScrollView(props, ref) {
  return <ScrollView scrollEventThrottle={16} {...props} ref={ref} />
})
